import useSWR from "swr";
import { fetcher, fmt, fmtPx } from "@/lib/api";

export function LiquidationMap({ symbol }) {
  const { data, error } = useSWR(symbol ? `/market/liquidations?symbol=${symbol}` : null, fetcher, { refreshInterval: 60000 });
  if (error) return <div className="panel p-3 text-xs text-[#5e6673]" data-testid="liquidation-map">Liquidation data unavailable</div>;
  if (!data) return <div className="panel p-3 text-xs text-[#5e6673]">Loading liquidations…</div>;
  const buckets = data.buckets.slice().reverse();
  const max = Math.max(1, ...data.buckets.map((b) => b.long_liq_usd + b.short_liq_usd));
  const bias = { shorts_squeezed: ["SHORTS SQUEEZED", "text-[#0ecb81] bg-[rgba(14,203,129,0.12)]"], longs_flushed: ["LONGS FLUSHED", "text-[#f6465d] bg-[rgba(246,70,93,0.12)]"], balanced: ["BALANCED", "text-[#848e9c] bg-[#2b3139]"] }[data.bias];
  const above = data.squeeze_zones.above[0], below = data.squeeze_zones.below[0];
  const usd = (v) => (v >= 1e6 ? `$${fmt(v / 1e6, 1)}M` : `$${fmt(v / 1e3, 0)}K`);
  return (
    <div className="panel p-3" data-testid="liquidation-map">
      <div className="flex items-center justify-between mb-2">
        <div className="panel-title">Liquidation Map · {symbol} <span className="text-[#5e6673] normal-case tracking-normal">last {data.events} events · {data.bucket_pct}% buckets · {data.source}</span></div>
        <span className={`pill ${bias[1]}`} data-testid="liquidation-bias">{bias[0]}</span>
      </div>
      <div className="grid grid-cols-3 gap-2 text-[11px] mb-2">
        <div><div className="panel-title">Long liq</div><div className="mono down mt-0.5">{usd(data.total_long_liq_usd)}</div></div>
        <div><div className="panel-title">Short liq</div><div className="mono up mt-0.5">{usd(data.total_short_liq_usd)}</div></div>
        <div><div className="panel-title">Squeeze zones</div><div className="mono text-white mt-0.5 text-[10px]" data-testid="squeeze-zones">{above ? `↑ ${fmtPx(above.price)}` : "↑ —"} · {below ? `↓ ${fmtPx(below.price)}` : "↓ —"}</div></div>
      </div>
      <div className="space-y-[2px]" data-testid="liquidation-buckets">
        {buckets.length === 0 && <div className="text-xs text-[#5e6673] text-center py-3">No recent liquidations</div>}
        {buckets.map((b, i) => {
          const tot = b.long_liq_usd + b.short_liq_usd;
          const isPrice = (i === buckets.length - 1 || buckets[i + 1].price < data.price) && b.price >= data.price;
          return (
            <div key={b.price}>
              <div className="flex items-center gap-2 mono text-[10px]">
                <span className={`w-16 text-right ${b.price > data.price ? "text-[#0ecb81]" : "text-[#f6465d]"}`}>{fmtPx(b.price)}</span>
                <div className="flex-1 h-3 bg-[#1e2329] rounded-[2px] overflow-hidden flex">
                  <div className="h-full bg-[#0ecb81]/70" style={{ width: `${(b.short_liq_usd / max) * 100}%` }} title={`short liq ${usd(b.short_liq_usd)}`} />
                  <div className="h-full bg-[#f6465d]/70" style={{ width: `${(b.long_liq_usd / max) * 100}%` }} title={`long liq ${usd(b.long_liq_usd)}`} />
                </div>
                <span className="w-14 text-right text-[#848e9c]">{usd(tot)}</span>
              </div>
              {isPrice && <div className="flex items-center gap-2 my-[1px]"><span className="w-16 text-right text-[9px] mono text-[#fcd535]">{fmtPx(data.price)}</span><div className="flex-1 border-t border-dashed border-[#fcd535]/60" /><span className="w-14 text-[9px] text-[#fcd535]">price</span></div>}
            </div>
          );
        })}
      </div>
      <div className="text-[10px] text-[#5e6673] mt-2">Green = shorts liquidated (buy pressure), red = longs liquidated (sell pressure). Dense clusters just beyond price act as magnets and squeeze zones; the AI receives the top clusters as FACTs.</div>
    </div>
  );
}
